import { firstValueFrom } from "rxjs";
import { apiService } from "../Services/apiService";
import { session } from "./session";
import { isExpired } from "./jwt";

// cookie helper (same as in apiService to avoid import cycles)
function hasCookie(name: string): boolean {
  if (typeof document === "undefined") return false;
  return document.cookie.split("; ").some((c) => c.startsWith(name + "="));
}

function canRefreshAtBoot(): boolean {
  // If you keep refresh token in storage for mobile:
  const storedRt = sessionStorage.getItem("refreshToken");
  // If web uses httpOnly cookie:
  const cookieRt =
    hasCookie("rt") ||
    hasCookie("refreshToken") ||
    hasCookie(".AspNetCore.Refresh");
  return !!storedRt || cookieRt;
}

/**
 * Call this once before rendering your app (after ApiConfig.load()).
 * - If an access token is missing or is expired/near-expiry, it silently refreshes (cookie for web; body for mobile if your apiService is wired for that).
 * - If refresh fails, it clears the local session.
 * - If there is no refresh token/cookie available (anonymous user), it does nothing.
 */
export async function bootstrapSession() {
  try {
    const access = (session as any).getAccess?.() ?? null;

    // If we already have a non-expired token, nothing to do.
    if (access && !isExpired(access, 30)) return;

    // Only try refresh if we actually have a refresh token/cookie
    if (!canRefreshAtBoot()) return;

    try {
      const refreshed = await firstValueFrom(apiService.refreshNow());
      const newAccess =
        (refreshed as any)?.accessToken ??
        (refreshed as any)?.data?.accessToken ??
        "";

      if (!newAccess) {
        (session as any).clear?.();
        return;
      }

      // Persist refreshed access token; keep existing user object
      try {
        // Preferred modern signature
        (session as any).save?.({
          accessToken: newAccess,
          user: (session as any).getUser?.() ?? undefined,
        });
      } catch {
        // Back-compat with legacy signature
        (session as any).save?.({
          token: newAccess,
          refreshToken: undefined,
          user: (session as any).getUser?.() ?? undefined,
        });
      }
    } catch {
      // Refresh failed → clear local session
      (session as any).clear?.();
    }
  } catch {
    (session as any).clear?.();
  }
}
